(ns dashboard.views.questionnaire.list.view
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.common :as common]
            [dashboard.components.breadcrumbs :refer [breadcrumbs]]
            [dashboard.components.confirm-delete-file-modal :as modal]
            [dashboard.components.file-upload.view :as file-upload-view]
            [dashboard.components.paginated-list.view :as paginated-list]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [dashboard.utils :as utils]
            [dashboard.views.questionnaire.question-list.config :as question-list-config]
            [dashboard.views.workbook-workspace.view :as workspace]
            [dashboard.services.questionnaires :as questionnaires]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]))

(def controllers
  [{:start (fn [_]
             (rf/dispatch [::questionnaire-list/get]))}])

(def confirm-delete-questionnaire-modal
  (modal/confirm-delete-file-modal
    {:on-confirm (fn [{:keys [id filename custom-name]}]
                   (rf/dispatch [::questionnaire-list/on-confirm-delete-questionnaire
                                 id
                                 (or custom-name filename)]))}))

(defn prompt-confirm-delete
  "On click handler to prompt delete questionnaire confirmation."
  [{:keys [ctx component]
    :or   {component #'confirm-delete-questionnaire-modal}}]
  (rf/dispatch [::ui/push-modal
                {:ctx       ctx
                 :component component}]))

(def created-column
  {:label    "Created"
   :id       :created
   :sort-key :created
   :render   (fn [{:keys [created]}]
               (common/format-date created))})

(def name-column
  {:label    "Name"
   :id       :name
   :title    "Name of the questionnaire. For questionnaires created from workbooks,
           if no user-created name present, shows the filename as name."
   :sort-key (fn [{:keys [custom-name filename url]}]
               [custom-name filename url])
   :render   (fn [{:keys [custom-name filename id]}]
             [:a.text-layout
              {:href  (rfe/href question-list-config/view-id {:questionnaire-id id})
               :title "Click to open the questionnaire"}
              (or custom-name filename)])})

(def question-count-column
  {:label "Questions"
   :id :question-count
   :title "Number of questions in the questionnaire"
   :sort-key :question-count
   :render (fn [{:keys [question-count]}]
             [:div question-count])})

(def status-column
  {:label    "Status"
   :id       :status
   :title    "Status of the questionnaire.
              The \"Approved\" status makes the selected answers
              of the questionnaire available for quick search and inference."
   :sort-key :status
   :render   (fn [{:keys [status]}]
               [:span.tag
                {:class (case status
                          "approved"    "is-success"
                          "in-progress" "is-warning"
                          "")}
                (if status
                  (str/capitalize status)
                  "To-do")])})

(def questionnaire-actions-column
  {:label   ""
   :id      :questionnaire-actions
   :classes ["is-narrow"]
   :render  (fn [{:keys [custom-name filename url id] questionnaire-type :type :as ctx}]
              (let [has-custom (and custom-name
                                    (not= custom-name filename))]
                [:span.level
                 [:a.is-size-7.mr-2
                  [:button.button.is-rounded.is-small.is-secondary
                   {:on-click (fn [_]
                                (rf/dispatch
                                 [::ui/push-modal
                                  {:ctx {:filename filename
                                         :questionnaire-id id}
                                   :content-props {:style {:width "90vw" :height "90vh" :max-height "90vh" :padding 0}}
                                   :component workspace/workbook-modal}]))
                    :title "Open Workbook and Update Worksheets"}
                   [:span.icon>i.fas.fa-table]]]
                 (cond
                   filename
                   [:button.button.is-rounded.is-small.is-secondary
                    {:title "View Original Document"
                     :class (when has-custom "is-size-7")
                     :on-click #(rf/dispatch [::questionnaires/fetch-file-preview-url
                                              filename])}
                    [:span.icon>i.fas.fa-file]]

                   url
                   [:a.is-size-7
                    {:href   url
                     :target "_blank"
                     :title "Click to open document via portal link, in a new tab"}
                    [:button.button.is-rounded.is-small.is-secondary
                     [:span.icon>i.fas.fa-link]]])

                 (when (not= "one-off" questionnaire-type)
                   [:button.button.is-danger.is-light.is-rounded.is-small.ml-2
                    {:on-click #(prompt-confirm-delete {:ctx ctx})
                     :title    "Delete questionnaire"}
                    [:span.icon>i.fas.fa-trash]])]))})

(def template-questionnaire-actions-column
  {:label   ""
   :id      :template-questionnaire-actions
   :classes ["is-narrow"]
   :render  (fn [{:keys [filename has-custom url]
                  :view/keys [features-enabled] :as ctx}]
              [:span.level
               (cond
                 filename
                 [:button.button.is-light.is-rounded.is-small
                  {:title "View Original Document"
                   :class (when has-custom "is-size-7")
                   :on-click #(rf/dispatch [::questionnaires/fetch-file-preview-url
                                            filename])}
                  [:span.icon>i.fas.fa-file]]

                 url
                 [:a.is-size-7
                  {:href url
                   :target "_blank"
                   :title "Click to open document via portal link, in a new tab"}
                  [:button.button.is-light.is-rounded.is-small
                   [:span.icon>i.fas.fa-link]]])

               [:button.button.is-danger.is-light.is-rounded.is-small.ml-2
                {:on-click #(prompt-confirm-delete {:ctx ctx})
                 :title (if (contains? features-enabled :structured-analysis-creation)
                          "Delete template questionnaire"
                          "Delete the local cached copy of the template.\n\n
                           The template will be fetched again when synchronizing structured analyses,
                           so this is more of an \"escape hatch\" to work around possible
                           issues in synchronization.")}
                [:span.icon>i.fas.fa-trash]]])})

(defn upload-questionnaire-modal [{{:keys [pred]} :ctx}]
  (let [items @(rf/subscribe [::questionnaire-list/data pred])]
    [file-upload-view/upload-modal
     {:title "Upload questionnaires"
      :file-type :questionnaires
      :uploaded-files items
      :accepted-file-formats ".xlsx, .xlsm"
      :success-message [:div.notification.is-info
                        [:h6.title.is-6
                         [:span.icon>i.fas.fa-solid.fa-info]
                         " Next Step: "]
                        [:div.level
                         "After importing questionnaires, visit Workbooks to link columns to questions, responses and more.
                          Doing this will add question and answer data into questionnaires, answer bank and question lab."]]}]))

(defn Templates [structured-analysis-creation-enabled? template-questionnaires]
  (let [[visible set-visible] (react/useState structured-analysis-creation-enabled?)]
    [:div
     (if structured-analysis-creation-enabled?
       [:div
        [:p.title.is-3 "Template questionnaires"]
        [:p "Questionnaires containing question to structured Analyses mappings,
             to aid in answering questionnaires, with similar questions"]]
       [:div.is-flex
        [:p "Templates synchronized from the master environment during structured analysis synchronization"]
        [:button-button.is-small.is-rounded
         {:title "Toggle visibility of template listing"
          :on-click #(set-visible (not visible))}
         (if visible
           [:span.icon>i.fas.fa-chevron-down]
           [:span.icon>i.fas.fa-chevron-right])]])
     (when visible
       [:f> paginated-list/paginated-list
        {:view-config-path [:view/template-questionnaire-list]
         :header-content (when structured-analysis-creation-enabled?
                           [:a.button.is-primary.mx-3.is-rounded.is-fullwidth
                            {:on-click #(rf/dispatch [::ui/push-modal
                                                      {:component #'upload-questionnaire-modal
                                                       :ctx {:pred :template}}])
                             :title "To import questionnaire from an Excel file,\nclick to upload an Excel file"}
                            "Upload"])
         :hide-header? (not structured-analysis-creation-enabled?)
         :columns [created-column
                   name-column
                   question-count-column
                   {:label "Analysed questions"
                    :id :structured-count
                    :title "Questions with structured analysis linked to the question"
                    :sort-key :questions-with-structured-analysis
                    :render :questions-with-structured-analysis}
                   template-questionnaire-actions-column]}
        {:items template-questionnaires
         :loading @(rf/subscribe [::questionnaire-list/loading])
         :default-sort-keyfn :created
         :default-sort-order :desc}])]))

(defn view []
  (let [features-enabled @(rf/subscribe [::common-subs/features-enabled])
        questionnaires (map #(assoc %
                                    :view/features-enabled features-enabled
                                    :paginated-list/row-key (:id %))
                            @(rf/subscribe [::questionnaire-list/data]))
        template-questionnaires (filter :template questionnaires)
        questionnaires (remove #(:template %) questionnaires)
        structured-analysis-creation-enabled? (contains? features-enabled :structured-analysis-creation)
        upload-button [:a.button.is-primary.mx-3.is-rounded.is-fullwidth
                       {:on-click #(rf/dispatch [::ui/push-modal {:component #'upload-questionnaire-modal
                                                                  :ctx {:pred (comp not :template)}}])
                        :title "To import questionnaire from an Excel file,\nclick to upload an Excel file"}
                       "Upload"]]
    [:div
     [breadcrumbs {:items [{:href (rfe/href :route.view/home)
                            :label "Dashboard"}
                           {:href (rfe/href :route.view/questionnaires)
                            :label "Questionnaires"
                            :active? true}]}]
     [:div.section
      (when @(rf/subscribe [::common-subs/feature-enabled :applied-analysis-creation])
        [:<>
         [:p.title.is-3 "Questionnaires"]
         [:f> paginated-list/paginated-list
          {:view-config-path [:view/questionnaire-list]
           :header-content upload-button
           :columns [created-column
                     status-column
                     name-column
                     question-count-column
                     {:label "Analysed questions"
                      :id :applied-count
                      :title "Questions with applied strutured analysis linked to the selected answer"
                      :sort-key :questions-with-applied-structured-analysis
                      :render :questions-with-applied-structured-analysis}
                     questionnaire-actions-column]
           :search-enabled true}
          {:items questionnaires
           :loading @(rf/subscribe [::questionnaire-list/loading])
           :default-sort-keyfn :created
           :default-sort-order :desc
           :data-filter (fn [search-query {:keys [custom-name filename]}]
                          (utils/match-search-query search-query
                                                    (fn [query-word]
                                                      (or (utils/str-includes-icase? custom-name query-word)
                                                          (utils/str-includes-icase? filename query-word)))))}]])
      (when (or structured-analysis-creation-enabled?
                (seq template-questionnaires))
        [:f> Templates structured-analysis-creation-enabled? template-questionnaires])]]))
